import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AuthService } from "../../services/auth/auth.service";
import { ConfigService } from "../../services/config.service";
import versionInfo from '../../../environments/version-info.json';
import { DatePipe } from "@angular/common";
import { ConsoleLogsCaptureService } from '../../services/console-logs-capture.service';

@Component({
  selector: 'app-main-sign-in',
  templateUrl: './main-sign-in.component.html',
  styleUrls: ['./main-sign-in.component.scss'],
  providers: [DatePipe],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MainSignInComponent implements OnInit {
  gitSHA!: string;
  buildTime!: string | null;
  isDevelopment: boolean = false;
  showGoogleAuth: boolean = false;
  showDevAuth: boolean = false;

  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private datePipe: DatePipe,
    private consoleLogsCapture: ConsoleLogsCaptureService
  ) {
    // Extract version info during construction to speed up initialization
    this.gitSHA = (versionInfo as any).gitSHA;
    this.buildTime = this.transformDate();
    
    // Check if we're in development mode
    this.isDevelopment = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1' ||
                        window.location.hostname.includes('172.30.') ||
                        window.location.hostname.includes('10.0.0.') ||
                        window.location.hostname === 'dev.finderwise.com';
  }

  ngOnInit(): void {
    console.log('[MainSignIn] Component initialized at', new Date().toISOString());
    console.log('[MainSignIn] Current URL:', window.location.href);
    console.log('[MainSignIn] Has token in URL:', window.location.href.includes('token='));
    
    // Check which auth providers are enabled
    const config = this.configService.getConfig();
    this.showGoogleAuth = config.authProviders?.google?.enabled || false;
    this.showDevAuth = config.authProviders?.devAuth?.enabled || false;
    
    console.log('[MainSignIn] Auth providers config:', {
      googleEnabled: this.showGoogleAuth,
      devAuthEnabled: this.showDevAuth,
      activeProvider: config.activeAuthProvider
    });
    
    // Auth service initialization is now handled by ServiceOrchestrator in AppComponent
    // This prevents the duplicate initialization issue described in FIN-0028
    console.log('[MainSignIn] Auth service should already be initialized by ServiceOrchestrator');
  }

  private transformDate() {
    let buildTime = (versionInfo as any).buildTime;
    if (buildTime) {
      const date = new Date(buildTime); // create Date object from buildTime
      return this.datePipe.transform(date, 'MMMM d, y, h:mm a'); // format the date to a localized string
    } else {
      return 'N/A';
    }
  }

  /**
   * Handle Google sign-in button click
   */
  signInWithGoogle(): void {
    const config = this.configService.getConfig();
    const googleConfig = config.authProviders?.google;

    if (!googleConfig || !googleConfig.enabled) {
      console.error('[MainSignIn] Google auth is not enabled');
      return;
    }

    console.log('[MainSignIn] Redirecting to Google OAuth:', googleConfig.authorizationUrl);
    window.location.href = googleConfig.authorizationUrl;
  }

  /**
   * Handle Dev Auth sign-in button click
   */
  signInWithDevAuth(): void {
    const config = this.configService.getConfig();
    const devAuthConfig = config.authProviders?.devAuth;

    if (!devAuthConfig || !devAuthConfig.enabled) {
      console.error('[MainSignIn] Dev Auth is not enabled');
      return;
    }

    console.log('[MainSignIn] Redirecting to Dev Auth OAuth:', devAuthConfig.authorizationUrl);
    sessionStorage.setItem('authStartTime', Date.now().toString());
    window.location.href = devAuthConfig.authorizationUrl;
  }

  /**
   * Copy console logs to clipboard (available in development environments)
   */
  copyConsoleLogsToClipboard(): void {
    this.consoleLogsCapture.copyConsoleLogsToClipboard();
  }
}